import { Badge, Button, Loader, Stack, Table, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AdminPortalPartnerRO } from '@routellers/types/dist/partners-portal/admin/admin-portal-partner.ro';
import { PortalPartnerStatusEnum } from '@routellers/types/dist/partners-portal/enum/portal-partner-status.enum';
import { PortalPartnerTypeEnum } from '@routellers/types/dist/partners-portal/enum/portal-partner-type.enum';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { EntityThumb } from '../components/entity-thumb';
import { ListPagination } from '../components/list-pagination';
import { PageHeader } from '../components/page-header';
import { PartnerCreateDialog } from '../editors/partner-create-dialog';
import { PartnerEditor } from '../editors/partner-editor';
import { slicePage } from '../utils/list';
import {
    portalPartnerStatusColors,
    portalPartnerStatusLabel,
} from '../utils/portal-partner-status';

export const AuthorsPage = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<AdminPortalPartnerRO[]>([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [editorPartner, setEditorPartner] = useState<AdminPortalPartnerRO | null>(null);
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);

        try {
            const { data } = await api.partnersPortalAdminApi.partners({
                type: PortalPartnerTypeEnum.AUTHOR,
            });

            setRows(data || []);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const pageRows = slicePage(rows, page);

    return (
        <Stack gap='md' style={{ flex: 1, minHeight: 0 }}>
            <PageHeader
                title={t('pages.authors.title')}
                action={
                    <Button onClick={() => setCreating(true)}>{t('pages.authors.create')}</Button>
                }
            />

            {loading ? (
                <Loader />
            ) : (
                <>
                    <Table.ScrollContainer minWidth={800}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{t('pages.authors.columns.name')}</Table.Th>
                                    <Table.Th ta='right'>
                                        {t('pages.authors.columns.commission')}
                                    </Table.Th>
                                    <Table.Th ta='right'>
                                        {t('pages.authors.columns.quests')}
                                    </Table.Th>
                                    <Table.Th ta='right'>
                                        {t('pages.authors.columns.inReview')}
                                    </Table.Th>
                                    <Table.Th>{t('pages.authors.columns.email')}</Table.Th>
                                    <Table.Th>{t('pages.authors.columns.status')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {pageRows.map((row) => (
                                    <Table.Tr
                                        key={row.id}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setEditorPartner(row)}
                                    >
                                        <Table.Td>
                                            <EntityThumb
                                                src={row.avatarUrl}
                                                name={row.name}
                                                subtitle={row.email}
                                            />
                                        </Table.Td>
                                        <Table.Td ta='right'>{row.commissionPercent}%</Table.Td>
                                        <Table.Td ta='right'>{row.questsCount || 0}</Table.Td>
                                        <Table.Td ta='right'>
                                            {row.questsInReviewCount ? (
                                                <Badge color='orange' variant='light'>
                                                    {row.questsInReviewCount}
                                                </Badge>
                                            ) : (
                                                0
                                            )}
                                        </Table.Td>
                                        <Table.Td>
                                            <Badge
                                                color={row.emailVerified ? 'green' : 'gray'}
                                                variant='light'
                                            >
                                                {row.emailVerified
                                                    ? t('pages.authors.verified')
                                                    : t('pages.authors.notVerified')}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            <Badge
                                                color={
                                                    portalPartnerStatusColors[
                                                        row.status ||
                                                            PortalPartnerStatusEnum.REGISTERED
                                                    ] || 'gray'
                                                }
                                                variant='light'
                                            >
                                                {portalPartnerStatusLabel(
                                                    t,
                                                    row.status ||
                                                        PortalPartnerStatusEnum.REGISTERED,
                                                )}
                                            </Badge>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                                {!pageRows.length && (
                                    <Table.Tr>
                                        <Table.Td colSpan={6}>
                                            <Text c='dimmed' ta='center' py='md'>
                                                {t('pages.authors.empty')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    <ListPagination page={page} totalItems={rows.length} onChange={setPage} />
                </>
            )}

            <PartnerCreateDialog
                opened={creating}
                type={PortalPartnerTypeEnum.AUTHOR}
                onClose={() => setCreating(false)}
                onCreated={() => {
                    load();
                }}
            />

            <PartnerEditor
                opened={editorPartner != null}
                partner={editorPartner}
                onClose={() => setEditorPartner(null)}
                onSaved={load}
                onDeleted={load}
            />
        </Stack>
    );
};
